import { Anchor, Flex, Stack, Text } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { ResearchPerfEvalCriteriaTable } from '@/module/ResearchPerfEval/components/ResearchPerfEvalCriteriaTable';
import { StartResearchPerfEvalButton } from '@/module/ResearchPerfEval/components/StartResearchPerfEvalButton';
import { VerifyResearchPerfEvalButton } from '@/module/ResearchPerfEval/components/VerifyResearchPerfEvalButton';
import { getAuthorResearchPerfEvalOptions } from '@/module/ResearchPerfEval/model/ResearchPerfEvalQuery';
import { ResearchPerfEvalReport } from '@/module/ResearchPerfEval/model/ResearchPerfEvalTypes';

import { Content, DateText, PendingSpinner } from '@/core/components';

import { RoleType } from '@feature/security';

interface AuthorResearchPerfEvalOverviewProps {
  evaluation: ResearchPerfEvalReport;
  canStartResearchPerfEval: boolean;
  userId: number;

  canVerifyResearchPerfEval?: boolean;
  currentRole: RoleType;
}

export function AuthorResearchPerfEvalOverview(props: AuthorResearchPerfEvalOverviewProps) {
  const { evaluation, canStartResearchPerfEval, userId, canVerifyResearchPerfEval } = props;
  const { t } = useTranslation();
  const { data } = useSuspenseQuery(getAuthorResearchPerfEvalOptions(evaluation.id, userId));
  const isCompleted = data?.completedAt != null;

  return (
    <Stack>
      <Content type="full">
        <Flex
          align="center"
          justify="space-between"
        >
          {data?.user != null ? (
            <Stack gap="4px">
              <Text>
                {data.user.lastName} {data.user.firstName}, {t(`jobTitleEnum.${data.user.jobTitle}`)}
              </Text>
              <Anchor href={`mailto:${data.user.email}`}>{data.user.email}</Anchor>
            </Stack>
          ) : (
            <div />
          )}
          <Stack>
            {canStartResearchPerfEval && !isCompleted && (
              <StartResearchPerfEvalButton
                alreadyStarted={data != null}
                evaluationId={evaluation.id}
              />
            )}
            {data != null && canVerifyResearchPerfEval && <VerifyResearchPerfEvalButton evaluationId={evaluation.id} />}
          </Stack>
        </Flex>
        {data != null && (
          <Text
            c="dimmed"
            size="sm"
          >
            {t('researchPerfEval.forSpecificOrderNo')}
          </Text>
        )}
      </Content>
      <Content type="full">
        {data == null && (
          <Text
            fw="500"
            c="dimmed"
            fs="italic"
          >
            {t('researchPerfEval.reportingNotStartedYet')}
          </Text>
        )}
        {data != null && (
          <Suspense fallback={<PendingSpinner h="20vh" />}>
            <Flex justify="space-between">
              <Flex>
                <Text mr="10px">{t('researchPerfEval.startedAt')}</Text>
                <DateText
                  showAs="date-time"
                  date={data.startedAt}
                />
              </Flex>
            </Flex>
          </Suspense>
        )}
      </Content>
      {data != null && <ResearchPerfEvalCriteriaTable evaluationId={evaluation.id} />}
    </Stack>
  );
}
